import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { getSessionId } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [cartLoaded, setCartLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [bulkDiscounts, setBulkDiscounts] = useState({ enabled: false, tiers: [] });
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  const sessionId = getSessionId();

  // Fetch bulk discount settings
  const fetchBulkDiscounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/bulk-discounts`);
      setBulkDiscounts(response.data);
    } catch (error) {
      console.error('Failed to fetch bulk discounts:', error);
    }
  }, []);

  useEffect(() => {
    fetchBulkDiscounts();
  }, [fetchBulkDiscounts]);

  // Calculate applied discount based on cart items
  useEffect(() => {
    if (!bulkDiscounts.enabled || !cart.items?.length) {
      setAppliedDiscount(null);
      return;
    }
    
    const totalItems = cart.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
    
    // Find the highest applicable discount tier
    const applicableTier = [...bulkDiscounts.tiers]
      .sort((a, b) => b.min_items - a.min_items)
      .find(tier => totalItems >= tier.min_items);
    
    if (applicableTier) {
      const discountAmount = (cart.total * applicableTier.discount_percent) / 100;
      setAppliedDiscount({
        tier: applicableTier,
        totalItems,
        discountAmount,
        discountedTotal: cart.total - discountAmount
      });
    } else {
      setAppliedDiscount(null);
    }
  }, [cart, bulkDiscounts]);

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const params = user ? {} : { session_id: sessionId };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API_URL}/cart`, { params, headers });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
      setCartLoaded(true);
    }
  }, [user, token, sessionId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (config, { openPanel = true } = {}) => {
    try {
      setLoading(true);
      const params = user ? {} : { session_id: sessionId };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API_URL}/cart/add`, config, { params, headers });
      setCart(response.data);
      if (openPanel) setIsOpen(true);
      return response.data;
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const replaceCartItem = async (itemId, config) => {
    try {
      setLoading(true);
      const params = user ? {} : { session_id: sessionId };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.put(`${API_URL}/cart/item/${itemId}/config`, config, { params, headers });
      setCart(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to update cart item:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    try {
      setLoading(true);
      const params = user ? { quantity } : { session_id: sessionId, quantity };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.put(`${API_URL}/cart/item/${itemId}`, null, { params, headers });
      setCart(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to update cart item:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      setLoading(true);
      const params = user ? {} : { session_id: sessionId };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.delete(`${API_URL}/cart/item/${itemId}`, { params, headers });
      setCart(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      const params = user ? {} : { session_id: sessionId };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.delete(`${API_URL}/cart/clear`, { params, headers });
      setCart({ items: [], total: 0 });
    } catch (error) {
      console.error('Failed to clear cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    cart,
    cartLoaded,
    loading,
    isOpen,
    setIsOpen,
    replaceCartItem,
    itemCount: cart.items?.length || 0,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refreshCart: fetchCart,
    bulkDiscounts,
    appliedDiscount,
    sessionId,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
